// =============================================================================
// MeuDriver — Verificação pública de certificado (/certificado/:numero)
// Usa a RPC verify_certificate (liberada para anon desde 2026-05); a página
// não existia — o número impresso no certificado não tinha onde ser conferido.
// =============================================================================

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BadgeCheck, SearchX, Search } from 'lucide-react'
import { useVerifyCertificate } from '@/hooks/useCertificates'
import { useSEO } from '@/hooks/useSEO'
import { formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

export default function CertificateVerifyPage() {
  const { numero } = useParams<{ numero?: string }>()
  const navigate = useNavigate()
  const [draft, setDraft] = useState(numero ?? '')
  const { data, isLoading, isError } = useVerifyCertificate(numero ?? null)
  useSEO({ title: 'Verificar certificado — MeuDriver', description: 'Confira a autenticidade de um certificado de aulas emitido no MeuDriver.' })

  return (
    <div className="mx-auto max-w-xl px-4 py-12 space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-extrabold text-gray-900">Verificar certificado</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Digite o número impresso no certificado para conferir se ele foi emitido pelo MeuDriver.
        </p>
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const n = draft.trim().toUpperCase()
          if (n) navigate(`/certificado/${encodeURIComponent(n)}`)
        }}
      >
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Ex.: MC-2026-000123" aria-label="Número do certificado" />
        <Button type="submit" className="gap-1.5"><Search className="h-4 w-4" /> Verificar</Button>
      </form>

      {numero && (
        isLoading ? (
          <Skeleton className="h-32 w-full rounded-2xl" />
        ) : isError ? (
          <p className="text-center text-sm text-red-600">Não foi possível consultar agora. Tente de novo em instantes.</p>
        ) : data?.valid ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 space-y-3">
            <p className="flex items-center gap-2 font-semibold text-emerald-800">
              <BadgeCheck className="h-5 w-5" /> Certificado válido
            </p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Número</dt><dd className="font-mono font-semibold">{data.certificate_number}</dd>
              <dt className="text-muted-foreground">Aluno</dt><dd>{data.student_name}</dd>
              <dt className="text-muted-foreground">Instrutor</dt><dd>{data.instructor_name}</dd>
              <dt className="text-muted-foreground">Aulas</dt><dd>{data.total_lessons} ({data.total_hours} h)</dd>
              {data.issued_at && (<><dt className="text-muted-foreground">Emitido em</dt><dd>{formatDate(data.issued_at, 'dd/MM/yyyy')}</dd></>)}
            </dl>
            <p className="text-xs text-muted-foreground">
              Por privacidade, mostramos só o primeiro nome do aluno.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
            <SearchX className="mx-auto h-8 w-8 text-red-500" />
            <p className="mt-2 font-semibold text-red-800">Certificado não encontrado</p>
            <p className="text-sm text-red-700">Confira o número. Se ele estiver certo, este certificado não foi emitido pelo MeuDriver.</p>
          </div>
        )
      )}
    </div>
  )
}
